import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Calendar, Search, User } from "lucide-react";

import { supabase } from "../../../../lib/supabase";
import { getMentorAssignments } from "../../data/mentorAssignments";

interface Mentee {
  id: string;
  name: string;
  email: string;
  status: "pending" | "active" | "completed";
  nextSession?: string;
  assignmentStart?: string;
  assignmentEnd?: string | null;
}

export function MenteesList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMentees = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get the current user's session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          setError("You must be logged in to view this page");
          return;
        }

        // Get the current user's ID
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          throw new Error("User not authenticated");
        }

        // Fetch mentor assignments
        const result = await getMentorAssignments(user.id);

        if (!result.success) {
          throw new Error(result.error || "Failed to fetch mentees");
        }

        if (!result.data || result.data.length === 0) {
          setMentees([]);
        } else {
          // Transform assignments to mentees format
          const menteesList: Mentee[] = result.data.map((assignment) => ({
            id: assignment.mentee.uuid,
            name: assignment.mentee.name,
            email: assignment.mentee.email,
            status: "active", // Default to active for now
            assignmentStart: assignment.start,
            assignmentEnd: assignment.end,
          }));

          setMentees(menteesList);
        }
      } catch (err) {
        console.error("Error fetching mentees:", err);
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMentees();
  }, []);

  const filteredMentees = mentees.filter(
    (mentee) =>
      mentee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentee.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: Mentee["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      {loading ? (
        <div className="py-12 text-center">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-vincer-500"></div>
          <p className="text-gray-600">Loading your mentees...</p>
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="mb-4 text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200"
          >
            Try Again
          </button>
        </div>
      ) : mentees.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto max-w-md rounded-lg bg-gray-50 p-8">
            <User className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium">No Mentees Assigned</h3>
            <p className="mb-4 text-gray-600">
              You don't have any mentees assigned to you yet.
            </p>
            <p className="text-sm text-gray-500">
              Check back later or contact support if you believe this is an
              error.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border bg-white shadow-sm">
          <div className="border-b p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search mentees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-vincer-500"
              />
            </div>
          </div>

          <div className="divide-y">
            {filteredMentees.map((mentee) => (
              <div
                key={mentee.id}
                className="p-6 transition-colors hover:bg-gray-50"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="mb-1 text-lg font-medium">{mentee.name}</h3>
                    <p className="text-gray-600">{mentee.email}</p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(mentee.status)}`}
                  >
                    {mentee.status.charAt(0).toUpperCase() +
                      mentee.status.slice(1)}
                  </span>
                </div>

                <div className="flex items-center gap-6">
                  {mentee.assignmentStart && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="size-4" />
                      <span>
                        Assigned since:{" "}
                        {format(
                          parseISO(mentee.assignmentStart),
                          "MMM d, yyyy"
                        )}
                        {mentee.assignmentEnd &&
                          ` until ${format(parseISO(mentee.assignmentEnd), "MMM d, yyyy")}`}
                      </span>
                    </div>
                  )}
                </div>
                {/* <Link to={`/mentor/pre-session?sessionId=${mentee.id}`}>
                  <button className="text-vincer-500 hover:text-vincer-400 font-medium">
                    View Details
                  </button>
                </Link> */}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
